import { makeAutoObservable } from 'mobx';
import type { MemoryEntry } from '@/app/lib/services/websocket-client.types';
import {
  isVisitorFact,
  MAX_VISITOR_FACTS,
  readVisitorContextUpdate,
  type VisitorContextUpdate,
} from '../../../../../shared/visitor-context.ts';

function normalizeSummary(summary: string): string {
  return summary.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** Browser-local context, partitioned by agent and the connected visitor identity. */
export class MemoryStore {
  memories: MemoryEntry[] = [];
  initialized = false;
  persistenceAvailable = true;
  #storageKey: string | null = null;
  #appliedSignals = new Set<string>();

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get count(): number {
    return this.memories.length;
  }
  get isEmpty(): boolean {
    return this.count === 0;
  }

  initialize(agentId: string, visitorId: string) {
    this.#appliedSignals.clear();
    if (!agentId || !visitorId) {
      this.memories = [];
      this.initialized = false;
      this.#storageKey = null;
      return;
    }
    this.#storageKey = `agentplace_memory_bank_v2_${encodeURIComponent(agentId)}_${encodeURIComponent(visitorId)}`;
    this.memories = [];
    this.persistenceAvailable = true;
    try {
      const stored = localStorage.getItem(this.#storageKey);
      const parsed: unknown = stored ? JSON.parse(stored) : [];
      if (Array.isArray(parsed)) {
        const seen = new Set<string>();
        this.memories = parsed
          .filter(isVisitorFact)
          .filter((fact) => {
            if (seen.has(fact.id)) {
              return false;
            }
            seen.add(fact.id);
            return true;
          })
          .slice(-MAX_VISITOR_FACTS);
      }
    } catch {
      this.persistenceAvailable = false;
      console.warn('[MemoryStore] Browser context could not be loaded');
    }
    this.initialized = true;
  }

  apply(update: VisitorContextUpdate, now = Date.now(), signalId?: string): boolean {
    if (signalId && this.#appliedSignals.has(signalId)) {
      return true;
    }
    const valid = readVisitorContextUpdate(update);
    if (!this.#storageKey || !valid) {
      return false;
    }
    let next = [...this.memories];
    if (valid.operation === 'clear') {
      next = [];
    } else if (valid.operation === 'remove') {
      next = next.filter((fact) => fact.id !== valid.id);
    } else {
      const duplicate = next.find(
        (fact) => normalizeSummary(fact.summary) === normalizeSummary(valid.summary),
      );
      const existing = valid.id ? next.find((fact) => fact.id === valid.id) : duplicate;
      if (valid.id && !existing) {
        return false;
      }
      if (!existing && next.length >= MAX_VISITOR_FACTS) {
        return false;
      }
      const fact = {
        id: existing?.id ?? crypto.randomUUID(),
        summary: valid.summary,
        timestamp: now,
      };
      next = next.filter((entry) => entry.id !== fact.id && entry.id !== duplicate?.id);
      next.push(fact);
    }
    try {
      localStorage.setItem(this.#storageKey, JSON.stringify(next));
      this.memories = next;
      this.persistenceAvailable = true;
      if (signalId) {
        this.#appliedSignals.add(signalId);
      }
      return true;
    } catch {
      this.persistenceAvailable = false;
      console.warn('[MemoryStore] Browser context update was not saved');
      return false;
    }
  }

  add(summary: string, now = Date.now()): boolean {
    return this.apply({ operation: 'upsert', summary }, now);
  }
  remove(id: string): boolean {
    return this.apply({ operation: 'remove', id });
  }
  clear(): boolean {
    return this.apply({ operation: 'clear' });
  }
  getAll(): MemoryEntry[] {
    return [...this.memories].sort((a, b) => a.timestamp - b.timestamp);
  }
  getById(id: string): MemoryEntry | undefined {
    return this.memories.find((fact) => fact.id === id);
  }
}
